import React from "react";

type IconButtonVariant = "filled" | "outlined" | "gradient" | "text";
type IconButtonSize = "sm" | "md" | "lg";
type IconButtonColor = "blue-gray" | "blue" | "red" | "green" | "orange" | "gray" | "white";

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  color?: IconButtonColor;
}

const variantClasses: Record<IconButtonVariant, (color: IconButtonColor) => string> = {
  filled: (color: IconButtonColor) => {
    const colorMap: Record<IconButtonColor, string> = {
      "blue-gray": "bg-blue-gray-500 text-white shadow-md hover:shadow-lg",
      blue: "bg-blue-500 text-white shadow-md hover:shadow-lg",
      red: "bg-red-500 text-white shadow-md hover:shadow-lg",
      green: "bg-green-500 text-white shadow-md hover:shadow-lg",
      orange: "bg-orange-500 text-white shadow-md hover:shadow-lg",
      gray: "bg-gray-500 text-white shadow-md hover:shadow-lg",
      white: "bg-white text-blue-gray-900 shadow-md hover:shadow-lg",
    };
    return colorMap[color];
  },
  outlined: (color: IconButtonColor) => {
    const colorMap: Record<IconButtonColor, string> = {
      "blue-gray": "border border-blue-gray-500 text-blue-gray-500 hover:bg-blue-gray-50",
      blue: "border border-blue-500 text-blue-500 hover:bg-blue-50",
      red: "border border-red-500 text-red-500 hover:bg-red-50",
      green: "border border-green-500 text-green-500 hover:bg-green-50",
      orange: "border border-orange-500 text-orange-500 hover:bg-orange-50",
      gray: "border border-gray-500 text-gray-500 hover:bg-gray-50",
      white: "border border-white text-white hover:bg-white/10",
    };
    return colorMap[color];
  },
  gradient: () => "bg-gradient-to-tr from-blue-600 to-blue-400 text-white shadow-md hover:shadow-lg",
  text: (color: IconButtonColor) => {
    const colorMap: Record<IconButtonColor, string> = {
      "blue-gray": "text-blue-gray-500 hover:bg-blue-gray-50",
      blue: "text-blue-500 hover:bg-blue-50",
      red: "text-red-500 hover:bg-red-50",
      green: "text-green-500 hover:bg-green-50",
      orange: "text-orange-500 hover:bg-orange-50",
      gray: "text-gray-500 hover:bg-gray-50",
      white: "text-white hover:bg-white/10",
    };
    return colorMap[color];
  },
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
};

export const IconButton: React.FC<IconButtonProps> = ({
  children,
  variant = "filled",
  size = "md",
  color = "blue-gray",
  className = "",
  ...props
}) => {
  const variantClass = variantClasses[variant](color);
  const sizeClass = sizeClasses[size];

  return (
    <button
      className={`relative align-middle select-none font-medium text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none rounded-lg inline-flex items-center justify-center ${variantClass} ${sizeClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};